/**
 * Utility for storing and retrieving the user's B2C authority
 * This is needed for silent token refresh to use the same authority as login
 */

const AUTHORITY_STORAGE_KEY = 'dispatch_user_authority';
const FLOW_TYPE_STORAGE_KEY = 'dispatch_user_flow_type';

export interface StoredAuthority {
  authority: string;
  flowType: 'federated' | 'local';
}

/**
 * Store the authority URL and flow type for the current user
 * @param authority The B2C authority URL from the API
 * @param flowType Whether the user is federated or local
 */
export function saveAuthorityToStorage(
  authority: string,
  flowType: 'federated' | 'local'
): void {
  try {
    const data: StoredAuthority = {
      authority,
      flowType,
    };
    localStorage.setItem(AUTHORITY_STORAGE_KEY, JSON.stringify(data));
    localStorage.setItem(FLOW_TYPE_STORAGE_KEY, flowType);
  } catch (error) {
    // Failed to save authority to storage
  }
}

/**
 * Retrieve the stored authority for the current user
 * @returns The stored authority data or null if not found
 */
export function getAuthorityFromStorage(): StoredAuthority | null {
  try {
    const stored = localStorage.getItem(AUTHORITY_STORAGE_KEY);
    if (!stored) return null;

    return JSON.parse(stored) as StoredAuthority;
  } catch (error) {
    // Failed to retrieve authority from storage
    return null;
  }
}

/**
 * Get just the authority URL from storage
 * @returns The authority URL or null if not found
 */
export function getStoredAuthority(): string | null {
  const data = getAuthorityFromStorage();
  return data?.authority || null;
}

/**
 * Get the flow type from storage
 * @returns The flow type or null if not found
 */
export function getStoredFlowType(): 'federated' | 'local' | null {
  try {
    const flowType = localStorage.getItem(FLOW_TYPE_STORAGE_KEY);
    return flowType as 'federated' | 'local' | null;
  } catch {
    return null;
  }
}

/**
 * Clear the stored authority (typically called on logout)
 */
export function clearAuthorityFromStorage(): void {
  try {
    localStorage.removeItem(AUTHORITY_STORAGE_KEY);
    localStorage.removeItem(FLOW_TYPE_STORAGE_KEY);
  } catch (error) {
    // Failed to clear authority from storage
  }
}
